import { useState } from 'react';
import { Mail, Lock, Loader2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

export const apiError = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || 'Something went wrong. Please try again.';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || '').filter(Boolean).join(' ');
  return d.msg || String(d);
};

export function EmailAuthForm({ isSignup, onDone }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('customer');
  const [busy, setBusy] = useState(false);
  const [agree, setAgree] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = isSignup
        ? await api.post('/auth/email/register', { email, password, name, role, accept_terms: agree })
        : await api.post('/auth/email/login', { email, password });
      onDone(data.token, data.user);
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="email-auth-form">
      {isSignup && (
        <>
          <input data-testid="email-auth-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" required
            className="w-full border-2 border-ink/15 rounded-lg px-3 h-11 text-sm outline-none focus:border-ink"/>
          <div className="flex gap-2 text-xs font-bold">
            {['customer', 'owner'].map((r) => (
              <button type="button" key={r} data-testid={`email-auth-role-${r}`} onClick={() => setRole(r)}
                className={`flex-1 h-9 rounded-lg border-2 ${role === r ? 'border-ink bg-ink text-white' : 'border-ink/15'}`}>
                {r === 'customer' ? 'I want to buy' : 'I own a shop'}
              </button>
            ))}
          </div>
        </>
      )}
      <label className="flex items-center gap-2 border-2 border-ink/15 rounded-lg px-3 h-11 focus-within:border-ink">
        <Mail className="w-4 h-4 text-ink/40"/>
        <input data-testid="email-auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required className="flex-1 outline-none text-sm bg-transparent"/>
      </label>
      <label className="flex items-center gap-2 border-2 border-ink/15 rounded-lg px-3 h-11 focus-within:border-ink">
        <Lock className="w-4 h-4 text-ink/40"/>
        <input data-testid="email-auth-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          placeholder={isSignup ? 'At least 8 characters' : 'Password'} minLength={isSignup ? 8 : 1} required className="flex-1 outline-none text-sm bg-transparent"/>
      </label>
      {isSignup && (
        <label className="flex items-start gap-2 text-xs text-ink/70">
          <input type="checkbox" data-testid="email-auth-terms" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5"/>
          <span>I agree to the <a href="/terms" target="_blank" rel="noreferrer" className="underline">Platform Terms</a> and <a href="/privacy" target="_blank" rel="noreferrer" className="underline">Privacy Policy</a>.</span>
        </label>
      )}
      <button data-testid="email-auth-submit" disabled={busy || (isSignup && !agree)} className="btn-primary w-full !py-2.5 text-sm">
        {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <KeyRound className="w-4 h-4"/>}{isSignup ? 'Create account' : 'Log in'}
      </button>
      {!isSignup && <p className="text-[11px] text-ink/60">Forgot your password? Log in with your phone code, or ask BharatCart support for a temporary password.</p>}
    </form>
  );
}

export function AddEmailLogin({ user }) {
  const [email, setEmail] = useState(user?.login_email || user?.email || '');
  const [password, setPassword] = useState('');
  const [current, setCurrent] = useState('');
  const [busy, setBusy] = useState(false);
  const has = !!user?.login_email;
  const save = async () => {
    setBusy(true);
    try {
      await api.post('/auth/email/set-password', { email, password, current_password: has ? current : null });
      setPassword(''); setCurrent('');
      toast.success('Email login is ready — you can now sign in with email and password too');
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="add-email-login" className="card-soft p-5 mt-5">
      <div className="font-heading font-bold text-base mb-1 flex items-center gap-2"><KeyRound className="w-4 h-4 text-[#0D47A1]"/>Email & password login</div>
      <p className="text-xs text-ink/60 mb-3">{user?.login_email ? `Active for ${user.login_email}. Set a new password below to change it.` : 'Add a second way to log in, useful when SMS codes are slow.'}</p>
      <div className="grid md:grid-cols-3 gap-2">
        {has && <input data-testid="add-email-login-current" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="Current password" className="pinp md:col-span-3"/>}
        <input data-testid="add-email-login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="pinp md:col-span-1"/>
        <input data-testid="add-email-login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password (8+ characters)" className="pinp"/>
        <button data-testid="add-email-login-save" onClick={save} disabled={busy || password.length < 8 || !email} className="btn-primary !py-2 text-sm">
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Lock className="w-4 h-4"/>}Save
        </button>
      </div>
    </div>
  );
}
